import React, { useState, useEffect } from "react";
import AdminNav from "../components/admin/AdminNav";
import { BEFORE_AFTER as DEFAULT_PORTFOLIO } from "../data/content";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, RefreshCw, Image, ArrowRight } from "lucide-react";

export default function AdminPortfolio() {
  const [items, setItems] = useState(DEFAULT_PORTFOLIO);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchContent = async () => {
    setLoading(true);
    try {
      const res = await api.get("/site/content");
      if (res.data?.before_after && Array.isArray(res.data.before_after) && res.data.before_after.length > 0) {
        setItems(res.data.before_after);
      } else {
        setItems(DEFAULT_PORTFOLIO);
      }
    } catch (err) {
      console.warn("Using default portfolio:", err);
      setItems(DEFAULT_PORTFOLIO);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const handleChange = (idx, field, val) => {
    const updated = [...items];
    updated[idx] = { ...updated[idx], [field]: val };
    setItems(updated);
  };

  const handleAddItem = () => {
    const newItem = {
      title: "Новая работа (модель/бренд)",
      before: "/images/before_1.jpg",
      after: "/images/after_1.jpg",
      note: "Описание выполненной реставрации или чистки"
    };
    setItems([...items, newItem]);
    toast.success("Добавлена новая карточка работы");
  };

  const handleDeleteItem = (idx) => {
    if (items.length <= 1) {
      toast.error("Должна остаться хотя бы одна работа в портфолио");
      return;
    }
    if (window.confirm("Удалить эту работу из портфолио на сайте?")) {
      setItems(items.filter((_, i) => i !== idx));
      toast.success("Работа удалена");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/admin/site/content", { before_after: items });
      toast.success("Портфолио «До / После» успешно сохранено на сайте!");
    } catch (err) {
      toast.error(formatApiError(err, "Ошибка при сохранении портфолио"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5EFE6] text-[#1A0E07]">
      <AdminNav />

      <main className="max-w-[1400px] mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 pb-6 border-b-2 border-[#4A1419]/20 mb-8">
          <div>
            <h1 className="font-heading text-3xl md:text-5xl text-[#4A1419] font-bold">
              Работы «До / После»
            </h1>
            <p className="text-sm text-[#6E5549] mt-1">
              Управляйте примерами работ в слайдере на главной странице сайта.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={fetchContent}
              disabled={loading}
              className="px-4 py-2.5 text-sm font-medium border-2 border-[#4A1419]/30 hover:border-[#4A1419] bg-[#F5EFE6] flex items-center gap-2"
            >
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
              <span>Обновить</span>
            </button>

            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2.5 text-sm font-bold bg-[#4A1419] text-[#F5EFE6] border-2 border-[#4A1419] hover:bg-[#320D10] flex items-center gap-2 shadow-md active:scale-95 transition-all"
            >
              {saving ? (
                <>
                  <RefreshCw size={16} className="animate-spin" />
                  <span>Сохранение...</span>
                </>
              ) : (
                <>
                  <Save size={16} />
                  <span>Сохранить на сайте</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* List of items */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {items.map((item, idx) => (
            <div key={idx} className="bg-white border-2 border-[#4A1419] p-6 shadow-sm flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between gap-3 pb-3 border-b border-[#4A1419]/15 mb-4">
                  <span className="font-heading text-xl font-bold text-[#4A1419]">
                    Работа #{idx + 1}
                  </span>
                  <button
                    onClick={() => handleDeleteItem(idx)}
                    className="p-1.5 text-red-600 hover:bg-red-50 transition-colors"
                    title="Удалить карточку"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>

                {/* Title */}
                <div className="mb-4">
                  <label className="block text-xs font-bold uppercase tracking-wider text-[#6E5549] mb-1">
                    Название пары / Изделие
                  </label>
                  <input
                    type="text"
                    value={item.title}
                    onChange={(e) => handleChange(idx, "title", e.target.value)}
                    placeholder="Например: Кроссовки BOSS"
                    className="w-full px-3 py-2 border-2 border-[#4A1419]/20 focus:border-[#4A1419] text-sm font-semibold focus:outline-none"
                  />
                </div>

                {/* Note */}
                <div className="mb-5">
                  <label className="block text-xs font-bold uppercase tracking-wider text-[#6E5549] mb-1">
                    Описание проделанной работы
                  </label>
                  <input
                    type="text"
                    value={item.note}
                    onChange={(e) => handleChange(idx, "note", e.target.value)}
                    placeholder="Например: Глубокая чистка и реставрация кожи"
                    className="w-full px-3 py-2 border-2 border-[#4A1419]/20 focus:border-[#4A1419] text-sm focus:outline-none"
                  />
                </div>

                {/* Images side-by-side */}
                <div className="grid grid-cols-2 gap-4 pt-2">
                  {/* Before */}
                  <div>
                    <span className="block text-[11px] font-bold uppercase tracking-wider text-red-800 mb-1">
                      Фото «ДО»
                    </span>
                    <div className="h-36 bg-[#F5EFE6] border border-[#4A1419]/30 flex items-center justify-center overflow-hidden mb-2 relative">
                      <img
                        src={item.before}
                        alt="До"
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <span className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-black/60 text-white text-[9px] font-bold uppercase">
                        До
                      </span>
                    </div>
                    <input
                      type="text"
                      value={item.before}
                      onChange={(e) => handleChange(idx, "before", e.target.value)}
                      placeholder="URL фото ДО"
                      className="w-full px-2 py-1 text-xs border border-[#4A1419]/20 focus:outline-none"
                    />
                  </div>

                  {/* After */}
                  <div>
                    <span className="block text-[11px] font-bold uppercase tracking-wider text-emerald-800 mb-1">
                      Фото «ПОСЛЕ»
                    </span>
                    <div className="h-36 bg-[#F5EFE6] border border-[#4A1419]/30 flex items-center justify-center overflow-hidden mb-2 relative">
                      <img
                        src={item.after}
                        alt="После"
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <span className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-black/60 text-white text-[9px] font-bold uppercase">
                        После
                      </span>
                    </div>
                    <input
                      type="text"
                      value={item.after}
                      onChange={(e) => handleChange(idx, "after", e.target.value)}
                      placeholder="URL фото ПОСЛЕ"
                      className="w-full px-2 py-1 text-xs border border-[#4A1419]/20 focus:outline-none"
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Add item */}
        <div className="mt-8">
          <button
            onClick={handleAddItem}
            className="w-full py-4 text-xs md:text-sm font-bold uppercase tracking-wider bg-white text-[#4A1419] border-2 border-dashed border-[#4A1419]/60 hover:bg-[#FDFBF7] flex items-center justify-center gap-2 transition-colors"
          >
            <Plus size={16} />
            <span>Добавить новую работу в портфолио</span>
          </button>
        </div>
      </main>
    </div>
  );
}
